<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useAuthStore } from '@/stores/authStore'
import { useRoute, useRouter } from 'vue-router'

const props = defineProps({
  isMobileOpen: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close-mobile'])

const authStore = useAuthStore()
const route = useRoute()
const router = useRouter()

const isUserMenuOpen = ref(false)
const isCollapsed = ref(false)

// Tooltip State
const tooltip = ref({
  visible: false,
  text: '',
  style: { top: '0px', left: '0px' }
})

// Tooltip Methods
const showTooltip = (event, text) => {
  if (!isCollapsed.value) return;
  const rect = event.currentTarget.getBoundingClientRect();
  tooltip.value = {
    visible: true,
    text: text,
    style: {
      top: `${rect.top + (rect.height / 2)}px`,
      left: `${rect.right + 8}px`
    }
  }
}

const hideTooltip = () => {
  tooltip.value.visible = false;
}

const currentOrganization = computed(() => authStore.activeOrganization)
const allOrganizations = computed(() => authStore.organizations || [])

const switchOrganization = (organizationId) => {
  authStore.setActiveOrganization(organizationId)
  isUserMenuOpen.value = false
  router.push(`/organizations/${organizationId}/analytics`)
}

onMounted(() => {
  if (authStore.isAuthenticated) {
    authStore.fetchUser()
  }
})

watch(() => route.params.id, (newId) => {
  if (newId && authStore.activeOrganization?.id !== newId) {
    authStore.setActiveOrganization(newId)
  }
}, { immediate: true })

watch(() => route.path, () => {
  if (props.isMobileOpen) {
    emit('close-mobile')
  }
})

const navGroups = computed(() => {
  const groups = [
    {
      title: 'Account',
      items: [
        { name: 'Profile', path: '/settings', icon: 'M17.982 18.725A7.488 7.488 0 0 0 12 15.75a7.488 7.488 0 0 0-5.982 2.975m11.963 0a9 9 0 1 0-11.963 0m11.963 0A8.966 8.966 0 0 1 12 21a8.966 8.966 0 0 1-5.982-2.275M15 9.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z' },
        { name: 'Organizations', path: '/organizations', icon: 'M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Z' },
      ]
    }
  ]

  if (allOrganizations.value?.length > 0 && currentOrganization.value?.id) {
    const role = currentOrganization.value?.meta?.role;
    const allOrgItems = [
      { name: 'Users', path: `/organizations/${currentOrganization.value?.id}/users`, icon: 'M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z' },
      { name: 'Teams', path: `/organizations/${currentOrganization.value?.id}/teams`, icon: 'M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z' },
      { name: 'Billing', path: `/organizations/${currentOrganization.value?.id}/billing`, icon: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z' },
      { name: 'Analytics', path: `/organizations/${currentOrganization.value?.id}/analytics`, icon: 'M3.75 3v11.25A2.25 2.25 0 0 0 6 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0 1 18 16.5h-2.25m-7.5 0h7.5m-7.5 0-1 3m8.5-3 1 3m0 0 .5 1.5m-.5-1.5h-9.5m0 0-.5 1.5m.75-9 3-3 2.148 2.148A12.061 12.061 0 0 1 16.5 7.605' },
      { name: 'Settings', path: `/organizations/${currentOrganization.value?.id}/settings`, icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37.526.315 1.134.48 1.724.48zM15 12a3 3 0 11-6 0 3 3 0 016 0z' },
    ];

    let orgItems = [];
    if (role === 'organization_owner' || role === 'organization_admin') {
      orgItems = allOrgItems;
    } else if (role === 'organization_billing_admin') {
      orgItems = allOrgItems.filter(item => item.name === 'Billing');
    }

    if (orgItems.length > 0) {
      groups.unshift({
        title: 'Organization',
        items: orgItems
      });
    }
  }

  return groups
})
</script>

<template>
  <div v-show="isMobileOpen" @click="emit('close-mobile')" class="fixed inset-0 z-40 bg-gray-900/50 backdrop-blur-sm md:hidden transition-opacity"></div>

  <nav :class="[
    isCollapsed ? 'md:w-20 w-64' : 'w-64',
    isMobileOpen ? 'translate-x-0' : '-translate-x-full',
    'fixed inset-y-0 left-0 z-50 transform bg-zinc-100 md:relative md:translate-x-0',
    'text-gray-800 shrink-0 flex flex-col h-full border-r border-gray-100/50 transition-all duration-300 ease-in-out'
  ]">
    <div class="p-4 flex flex-col min-h-0 grow">

      <div :class="[
        'relative border-b border-gray-300/50 pb-3 flex transition-all duration-300',
        isCollapsed ? 'flex-col items-center gap-2' : 'flex-row items-center gap-1'
      ]">
        <div :class="[
          'flex items-center p-1.5 rounded-lg transition-all duration-300',
          isCollapsed ? 'justify-center w-full' : 'grow min-w-0'
        ]">
          <div class="flex items-center gap-2.5 min-w-0">
            <div class="w-8 h-8 rounded-lg border border-gray-200 bg-white flex items-center justify-center shrink-0">
              <svg width="20" height="20" viewBox="0 0 256 256">
                <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                  <g>
                    <rect stroke="#000000" stroke-width="16" x="38" y="38" width="180" height="180" rx="18"></rect>
                    <rect fill="#000000" x="91" y="96" width="24" height="64" rx="12"></rect>
                    <rect fill="#000000" x="139" y="96" width="24" height="64" rx="12"></rect>
                    <rect fill="#000000" x="95" y="116" width="64" height="24" rx="12"></rect>
                    <rect fill="#000000" x="79" y="0" width="24" height="64" rx="12"></rect>
                    <rect fill="#000000" x="152" y="0" width="24" height="64" rx="12"></rect>
                    <rect fill="#000000" x="80" y="192" width="24" height="64" rx="12"></rect>
                    <rect fill="#000000" x="152" y="192" width="24" height="64" rx="12"></rect>
                    <rect fill="#000000" x="0" y="80" width="64" height="24" rx="12"></rect>
                    <rect fill="#000000" x="0" y="152" width="64" height="24" rx="12"></rect>
                    <rect fill="#000000" x="192" y="80" width="64" height="24" rx="12"></rect>
                    <rect fill="#000000" x="192" y="152" width="64" height="24" rx="12"></rect>
                  </g>
                </g>
              </svg>
            </div>
            <span v-if="!isCollapsed" class="text-sm font-bold text-gray-900 truncate tracking-tight">HasMCP</span>
          </div>
        </div>

        <button @click="isCollapsed = !isCollapsed" :class="[
          'hidden md:inline-flex items-center justify-center whitespace-nowrap text-gray-400 hover:text-gray-600 hover:bg-gray-100 outline-none focus-visible:ring-2 focus-visible:ring-gray-200 rounded-[10px] size-8 shrink-0 transition-all duration-200',
          isCollapsed ? '' : 'pl-4'
        ]" :title="isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none"
            stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"
            :class="['size-4.5 transition-transform duration-300', isCollapsed ? 'rotate-180' : '']">
            <path
              d="M15.25 13.25V4.75C15.25 3.64543 14.3546 2.75 13.25 2.75L4.75 2.75C3.64543 2.75 2.75 3.64543 2.75 4.75V13.25C2.75 14.3546 3.64543 15.25 4.75 15.25H13.25C14.3546 15.25 15.25 14.3546 15.25 13.25Z" />
            <path d="M5.75 5.75V12.25" />
            <path d="M11.5 6.75L9.25 9L11.5 11.25" />
          </svg>
        </button>
      </div>

      <router-link v-if="currentOrganization?.status === 'active'" :to="'/app/' + currentOrganization?.id"
        @mouseenter="showTooltip($event, 'Go to App')" @mouseleave="hideTooltip"
        class="flex items-center gap-2 mt-6 mb-2 px-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
        :class="isCollapsed ? 'justify-center' : ''"> <svg class="w-4 h-4 text-gray-400 shrink-0" fill="none"
          viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
        <span v-if="!isCollapsed">Go to App</span> </router-link>

      <div class="space-y-6 mt-4 overflow-y-auto custom-scrollbar" style="max-height: calc(100vh - 180px);">
        <div v-for="group in navGroups" :key="group.title">
          <h3 v-if="!isCollapsed" class="px-2 text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-2">
            {{ group.title }}
          </h3>
          <div class="space-y-0.5 tracking-wide">
            <router-link v-for="item in group.items" :key="item.name" :to="item.path"
              @mouseenter="showTooltip($event, item.name)" @mouseleave="hideTooltip"
              class="flex items-center gap-2.5 px-2 py-1.5 rounded-md text-sm font-normal transition-all duration-150 group"
              active-class="bg-gray-200 text-black" :class="[
                isCollapsed ? 'justify-center' : '',
                $route.path === item.path
                  ? 'bg-gray-100 text-black'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              ]"> <svg class="w-[18px] h-[18px] transition-colors shrink-0"
                :class="$route.path === item.path ? 'text-black' : 'text-gray-400 group-hover:text-gray-500'"
                fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" :d="item.icon" />
              </svg>
              <span v-if="!isCollapsed">{{ item.name }}</span> </router-link>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-auto p-4">

      <div class="relative">

        <div v-if="isUserMenuOpen" :class="[
          'absolute bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden ring-1 ring-black/5 transform transition-all duration-200 z-50',
          isCollapsed
            ? 'left-full bottom-0 ml-2 w-64 origin-bottom-left'
            : 'bottom-full left-0 w-full mb-3 origin-bottom'
        ]">
          <div class="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
            <p class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-0.5">Signed in as</p>
            <p class="text-sm font-medium text-gray-900 truncate" :title="authStore.user?.email">{{
              authStore.user?.email || 'Loading...' }}</p>
          </div>

          <div v-if="allOrganizations?.length > 1" class="py-1 border-b border-gray-100">
            <div class="px-4 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">Switch Organization
            </div>
            <div class="max-h-48 overflow-y-auto custom-scrollbar px-1">
              <button v-for="org in allOrganizations" :key="org.id" @click="switchOrganization(org.id)"
                class="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors group">
                <span class="truncate font-medium" :class="currentOrganization?.id === org.id ? 'text-gray-900' : ''">{{
                  org.name }} <small
                    class="inline-flex items-center px-0.5 py-0.5 rounded text-xs font-light border bg-gray-50 text-gray-800 border-gray-200">{{
                      org?.meta?.role.replace('organization_', '').split('_').join(' ')
                    }}</small></span>
                <svg v-if="currentOrganization?.id === org.id" class="w-4 h-4 text-black" fill="none"
                  viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
              </button>
            </div>
          </div>

          <div class="p-1">
            <button @click="authStore.signout()"
              class="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
              <svg class="w-4.5 h-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Log out
            </button>
          </div>
        </div>

        <button @click="isUserMenuOpen = !isUserMenuOpen"
          class="flex items-center gap-3 w-full p-2 -mx-2 rounded-xl hover:bg-gray-100/80 transition-all duration-200 group outline-none focus-visible:ring-2 focus-visible:ring-gray-200"
          :class="isCollapsed ? 'justify-center mx-0' : ''">

          <div class="relative shrink-0">
            <div
              class="w-9 h-9 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center text-gray-700 font-bold text-sm overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
              <img v-if="authStore.user?.picture" :src="authStore.user.picture" class="w-full h-full object-cover"
                alt="Profile" />
              <span v-else>{{ authStore.user?.name?.charAt(0).toUpperCase() ||
                authStore.user?.email?.charAt(0).toUpperCase() || '?' }}</span>
            </div>
          </div>

          <div v-if="!isCollapsed" class="flex flex-col items-start overflow-hidden text-left min-w-0 flex-1">
            <span
              class="text-sm font-semibold text-gray-700 truncate w-full group-hover:text-gray-900 transition-colors">
              {{ authStore.user?.name || 'User' }}
            </span>
          </div>

          <svg v-if="!isCollapsed" class="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors"
            :class="isUserMenuOpen ? '' : 'rotate-180'" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

    </div>

    <div v-if="tooltip.visible"
      class="fixed z-50 px-2.5 py-1.5 text-xs font-medium text-white bg-gray-900 rounded-md shadow-lg pointer-events-none transform -translate-y-1/2 whitespace-nowrap"
      :style="tooltip.style">
      {{ tooltip.text }}
    </div>

  </nav>
</template>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: transparent;
  border-radius: 20px;
}

.custom-scrollbar:hover::-webkit-scrollbar-thumb {
  background-color: #e5e7eb;
}

/* Ensure icons don't shrink during transition */
svg {
  flex-shrink: 0;
}
</style>